import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  Grid,
  Slider,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Chip,
  Alert,
  InputAdornment,
  FormControl,
  FormLabel,
  Badge,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  AttachMoney as PriceIcon,
  AccessTime as HourlyIcon,
  Today as DailyIcon,
  DateRange as WeeklyIcon,
  CalendarMonth as MonthlyIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../context/CurrencyContext';

/**
 * 💰 FILTRE DE PRIX COMPLET
 * 
 * Fonctionnalités:
 * ✅ 4 types de tarification (horaire, journalier, hebdomadaire, mensuel)
 * ✅ Sliders intelligents avec auto-ajustement
 * ✅ Gestion des devises internationales
 * ✅ Interface fluide et responsive
 * ✅ Validation et gestion d'erreurs
 * ✅ Internationalisation complète
 */
const PriceFilter = ({ 
  onPriceChange, 
  initialPriceType = 'daily',
  isExpanded = false,
  onToggleExpand,
  selectedPriceRange = null,
  onNotification,
  externalFilters,
  disabled = false,
  forcedCurrency = null
}) => {
  const { t } = useTranslation();
  
  // Vérification de sécurité pour le contexte de devise
  let formatPrice, currency;
  try {
    const currencyContext = useCurrency();
    formatPrice = currencyContext?.formatPrice || ((value) => `${value}`);
    currency = forcedCurrency || currencyContext?.currency || 'EUR';
  } catch (error) {
    console.warn('PriceFilter: Contexte de devise non disponible, utilisation des valeurs par défaut');
    formatPrice = (value) => `${value}`;
    currency = forcedCurrency || 'EUR';
  }

  // 📊 ÉTATS LOCAUX - Avec valeurs par défaut sécurisées
  const [priceType, setPriceType] = useState(initialPriceType);
  const [priceRange, setPriceRange] = useState([0, 10000000]);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sliderRange, setSliderRange] = useState([0, 10000000]);
  const [priceRanges] = useState({
    hourly: [0, 10000000],
    daily: [0, 10000000],
    weekly: [0, 10000000],
    monthly: [0, 10000000]
  });
  const [errors, setErrors] = useState({});

  // 🎨 CONFIGURATION DES TYPES DE PRIX
  const priceTypes = [
    {
      value: 'hourly',
      label: t('filters.priceType.hourly', 'Horaire'),
      icon: <HourlyIcon sx={{ fontSize: 16 }} />,
      shortLabel: t('filters.priceUnit.hourly', '/h'),
      maxDefault: 10000000,
      step: 100,
      color: '#ff6b6b'
    },
    {
      value: 'daily',
      label: t('filters.priceType.daily', 'Journalier'),
      icon: <DailyIcon sx={{ fontSize: 16 }} />,
      shortLabel: t('filters.priceUnit.daily', '/jour'),
      maxDefault: 10000000,
      step: 500,
      color: '#4ecdc4'
    },
    {
      value: 'weekly',
      label: t('filters.priceType.weekly', 'Hebdomadaire'),
      icon: <WeeklyIcon sx={{ fontSize: 16 }} />,
      shortLabel: t('filters.priceUnit.weekly', '/sem'),
      maxDefault: 10000000,
      step: 1000,
      color: '#45b7d1'
    },
    {
      value: 'monthly',
      label: t('filters.priceType.monthly', 'Mensuel'),
      icon: <MonthlyIcon sx={{ fontSize: 16 }} />,
      shortLabel: t('filters.priceUnit.monthly', '/mois'),
      maxDefault: 10000000,
      step: 5000,
      color: '#96ceb4'
    }
  ];

  const currentPriceType = priceTypes.find(type => type.value === priceType);

  // 🔄 SYNCHRONISATION AVEC LES FILTRES SÉLECTIONNÉS - SIMPLIFIÉE
  useEffect(() => {
    if (selectedPriceRange) {
      const { type, min, max } = selectedPriceRange;
      if (type && type !== priceType) {
        setPriceType(type);
      }
      if (min !== undefined && min?.toString() !== minPrice) {
        setMinPrice(min?.toString() || '');
      }
      if (max !== undefined && max?.toString() !== maxPrice) {
        setMaxPrice(max?.toString() || '');
      }
      // Mise à jour du range seulement si nécessaire
      if ((min !== undefined || max !== undefined) && (min !== priceRange[0] || max !== priceRange[1])) {
        const currentPriceRanges = priceRanges[type || priceType];
        if (currentPriceRanges && Array.isArray(currentPriceRanges)) {
          setPriceRange([min || 0, max || currentPriceRanges[1]]);
        }
      }
    } else if (selectedPriceRange === null) {
      // 🧹 REMISE À ZÉRO QUAND selectedPriceRange DEVIENT NULL
      console.log('💰 [PriceFilter] Clearing price filters (selectedPriceRange is null)');
      setMinPrice('');
      setMaxPrice('');
      // Remettre le slider à sa valeur par défaut
      const defaultRange = priceRanges[priceType];
      if (defaultRange && Array.isArray(defaultRange)) {
        setPriceRange([0, defaultRange[1]]);
      }
    }
  }, [selectedPriceRange]); // Suppression des dépendances qui causent la boucle

  // ⚡ AJUSTEMENT AUTOMATIQUE DES RANGES - SIMPLIFIÉ
  useEffect(() => {
    const currentRange = priceRanges[priceType];
    
    // Vérification de sécurité
    if (!currentRange || !Array.isArray(currentRange)) {
      console.warn(`PriceFilter: Range invalide pour ${priceType}:`, currentRange);
      return;
    }
    
    setSliderRange(currentRange);
    
    // Ne réinitialiser que si les valeurs actuelles sont vraiment invalides
    const currentMin = Number(minPrice) || 0;
    const currentMax = Number(maxPrice) || currentRange[1];
    
    if (currentMax > currentRange[1] || currentMin < 0) {
      const newMin = Math.max(0, currentMin);
      const newMax = Math.min(currentRange[1], currentMax);
      setPriceRange([newMin, newMax]);
      setMinPrice(newMin.toString());
      setMaxPrice(newMax.toString());
    }
  }, [priceType]); // Seulement quand le type change

  // 🔄 GESTION DES FILTRES EXTERNES (recherches sauvegardées)
  useEffect(() => {
    if (externalFilters && (
      externalFilters.minPrice !== undefined || 
      externalFilters.maxPrice !== undefined || 
      externalFilters.priceType || 
      externalFilters.currency
    )) {
      console.log('💰 [PriceFilter] Processing external price filters:', {
        minPrice: externalFilters.minPrice,
        maxPrice: externalFilters.maxPrice,
        priceType: externalFilters.priceType,
        currency: externalFilters.currency
      });

      // Appliquer le type de prix si fourni
      if (externalFilters.priceType && externalFilters.priceType !== priceType) {
        setPriceType(externalFilters.priceType);
      }

      // Appliquer la devise si fournie (note: dans ce composant, currency est en lecture seule)
      if (externalFilters.currency && externalFilters.currency !== currency) {
        console.log('💰 [PriceFilter] Currency change detected but not implemented (currency is read-only from context)');
      }

      // Appliquer les prix
      if (externalFilters.minPrice !== undefined) {
        const minValue = Number(externalFilters.minPrice) || 0;
        setMinPrice(minValue.toString());
        
        // Mettre à jour le slider si nécessaire
        const currentMin = priceRange[0];
        if (currentMin !== minValue) {
          setPriceRange([minValue, priceRange[1]]);
        }
      }

      if (externalFilters.maxPrice !== undefined) {
        const maxValue = Number(externalFilters.maxPrice) || 0;
        setMaxPrice(maxValue.toString());
        
        // Mettre à jour le slider si nécessaire
        const currentMax = priceRange[1];
        if (currentMax !== maxValue) {
          setPriceRange([priceRange[0], maxValue]);
        }
      }

      console.log('💰 [PriceFilter] External filters applied successfully');
    }
  }, [externalFilters, priceType, currency, priceRange]);

  // 🎯 VALIDATION DES PRIX
  const validatePrices = useCallback((min, max) => {
    const errors = {};
    const numMin = Number(min);
    const numMax = Number(max);
    const maxAllowed = sliderRange[1];

    if (min && isNaN(numMin)) {
      errors.minPrice = t('filters.price.errors.invalidMin', 'Prix minimum invalide');
    }
    if (max && isNaN(numMax)) {
      errors.maxPrice = t('filters.price.errors.invalidMax', 'Prix maximum invalide');
    }
    if (min && !isNaN(numMin) && numMin < 0) {
      errors.minPrice = t('filters.price.errors.negativeMin', 'Le prix ne peut pas être négatif');
    }
    if (max && !isNaN(numMax) && numMax < 0) {
      errors.maxPrice = t('filters.price.errors.negativeMin', 'Le prix ne peut pas être négatif');
    }
    if (min && !isNaN(numMin) && numMin > maxAllowed) {
      errors.minPrice = t('filters.price.errors.exceedsMax', 'Dépasse le maximum autorisé ({{max}})', { max: maxAllowed });
    }
    if (max && !isNaN(numMax) && numMax > maxAllowed) {
      errors.maxPrice = t('filters.price.errors.exceedsMax', 'Dépasse le maximum autorisé ({{max}})', { max: maxAllowed });
    }
    if (min && max && !isNaN(numMin) && !isNaN(numMax) && numMin >= numMax) {
      errors.range = t('filters.price.errors.invalidRange', 'Le prix minimum doit être inférieur au maximum');
    }

    return errors;
  }, [t, sliderRange]);

  // 📱 GESTION DU CHANGEMENT DE TYPE
  const handlePriceTypeChange = useCallback((event, newType) => {
    if (newType && newType !== priceType) {
      setPriceType(newType);
      
      // Notifier immédiatement le changement au parent
      if (onPriceChange) {
        onPriceChange({
          priceType: newType,
          minPrice: minPrice ? Number(minPrice) : null,
          maxPrice: maxPrice ? Number(maxPrice) : null,
          currency
        });
      }
      
      // Notification du changement
      onNotification?.({
        type: 'info',
        title: t('filters.price.typeChanged.title', 'Type de tarification modifié'),
        message: t('filters.price.typeChanged.message', 'Recherche adaptée à la tarification {{type}}', {
          type: priceTypes.find(p => p.value === newType)?.label.toLowerCase()
        })
      });
    }
  }, [priceType, minPrice, maxPrice, currency, onPriceChange, onNotification, t, priceTypes]);

  // 📊 GESTION DU SLIDER
  const handleSliderChange = useCallback((event, newValue) => {
    setPriceRange(newValue);
    setMinPrice(newValue[0]?.toString() || '');
    setMaxPrice(newValue[1]?.toString() || '');
    setErrors({});
    
    // Notifier immédiatement le changement au parent
    if (onPriceChange) {
      onPriceChange({
        priceType,
        minPrice: newValue[0] || null,
        maxPrice: newValue[1] || null,
        currency
      });
    }
  }, [priceType, currency, onPriceChange]);

  // ⌨️ GESTION DES CHAMPS TEXTE - Avec délai pour éviter trop d'appels
  const handleMinPriceChange = useCallback((event) => {
    const raw = event.target.value;
    setMinPrice(raw);
    
    if (raw) {
      const numValue = Number(raw);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= sliderRange[1]) {
        setPriceRange([numValue, priceRange[1]]);
      }
    }
    
    const newErrors = validatePrices(raw, maxPrice);
    setErrors(newErrors);
    
    // Notifier le changement avec un délai seulement si pas d'erreurs
    if (Object.keys(newErrors).length === 0 && onPriceChange) {
      setTimeout(() => {
        onPriceChange({
          priceType,
          minPrice: raw ? Number(raw) : null,
          maxPrice: maxPrice ? Number(maxPrice) : null,
          currency
        });
      }, 500);
    }
  }, [priceRange, maxPrice, priceType, currency, onPriceChange, validatePrices, sliderRange]);

  const handleMaxPriceChange = useCallback((event) => {
    const raw = event.target.value;
    setMaxPrice(raw);
    
    if (raw) {
      const numValue = Number(raw);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= sliderRange[1]) {
        setPriceRange([priceRange[0], numValue]);
      }
    }
    
    const newErrors = validatePrices(minPrice, raw);
    setErrors(newErrors);
    
    // Notifier le changement avec un délai seulement si pas d'erreurs
    if (Object.keys(newErrors).length === 0 && onPriceChange) {
      setTimeout(() => {
        onPriceChange({
          priceType,
          minPrice: minPrice ? Number(minPrice) : null,
          maxPrice: raw ? Number(raw) : null,
          currency
        });
      }, 500);
    }
  }, [priceRange, minPrice, priceType, currency, onPriceChange, validatePrices, sliderRange]);

  // 🧹 RÉINITIALISATION
  const resetFilters = useCallback(() => {
    const defaultRange = priceRanges[priceType];
    
    // Vérification de sécurité
    if (!defaultRange || !Array.isArray(defaultRange)) {
      console.warn(`PriceFilter: Range par défaut invalide pour ${priceType}:`, defaultRange);
      return;
    }
    
    setPriceRange(defaultRange);
    setMinPrice('');
    setMaxPrice('');
    setErrors({});
    
    onPriceChange?.({
      priceType,
      minPrice: null,
      maxPrice: null,
      currency
    });

    onNotification?.({
      type: 'info',
      title: t('filters.price.reset.title', 'Filtre de prix réinitialisé'),
      message: t('filters.price.reset.message', 'Toutes les restrictions de prix ont été supprimées')
    });
  }, [priceRanges, priceType, onPriceChange, currency, onNotification, t]);

  // 🎨 ANIMATIONS
  const sectionVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: { 
      opacity: 1, 
      height: 'auto',
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  const chipVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.2 }
    }
  };

  // 📊 COMPTAGE DES FILTRES ACTIFS
  const getActiveFiltersCount = () => {
    let count = 0;
    if (minPrice && Number(minPrice) > 0) count++;
    
    // Vérification de sécurité pour maxPrice
    const currentPriceRanges = priceRanges[priceType];
    if (maxPrice && currentPriceRanges && Array.isArray(currentPriceRanges) && Number(maxPrice) < currentPriceRanges[1]) {
      count++;
    }
    
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Box sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      {/* 🎯 EN-TÊTE DE SECTION */}
      <Box
        sx={{
          p: 2,
          width: '100%',
          background: disabled
            ? 'rgba(0,0,0,0.04)'
            : isExpanded 
              ? 'var(--filter-secondary-gradient)'
              : 'transparent',
          color: disabled ? 'text.disabled' : isExpanded ? 'white' : 'inherit',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'var(--filter-transition-medium)',
          '&:hover': disabled ? {} : {
            background: isExpanded 
              ? 'var(--filter-secondary-gradient)'
              : 'rgba(79, 172, 254, 0.1)',
            transform: 'translateY(-1px)'
          }
        }}
        className={`filter-item-interactive ${isExpanded && !disabled ? 'filter-selected' : ''}`}
        onClick={disabled ? undefined : onToggleExpand}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PriceIcon sx={{ mr: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('filters.price.title', 'Prix')}
              {disabled && (
                <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic' }}>
                  ({t('filters.price.selectCountryFirst', 'Sélectionnez un pays d\'abord')})
                </Typography>
              )}
            </Typography>
            {!disabled && (
              <Badge 
                badgeContent={activeFiltersCount} 
                color="secondary"
                sx={{ ml: 2 }}
              />
            )}
            
            {/* 🏷️ DEVISE ACTIVE */}
            {!disabled && forcedCurrency && (
              <Chip
                label={forcedCurrency}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ ml: 1, fontWeight: 700, fontSize: '0.7rem' }}
              />
            )}
            
            {/* 🏷️ CHIP DU TYPE SÉLECTIONNÉ */}
            <AnimatePresence>
              {!disabled && priceType && (
                <motion.div
                  variants={chipVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  style={{ marginLeft: 8 }}
                >
                  <Chip
                    icon={currentPriceType?.icon}
                    label={currentPriceType?.label}
                    size="small"
                    sx={{
                      backgroundColor: `${currentPriceType?.color}20`,
                      color: currentPriceType?.color,
                      fontWeight: 'medium'
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          
          {!disabled && (
            <ExpandMoreIcon 
              sx={{ 
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease'
              }} 
            />
          )}
        </Box>
      </Box>

      {/* 📋 CONTENU DU FILTRE */}
      <Collapse in={isExpanded} timeout={300}>
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          animate={isExpanded ? "visible" : "hidden"}
        >
          <Box sx={{ p: 3 }}>
            {/* 🚨 AFFICHAGE DES ERREURS */}
            <AnimatePresence>
              {Object.keys(errors).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  style={{ marginBottom: 16 }}
                >
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {Object.values(errors)[0]}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <Grid container spacing={3}>
              {/* 📱 SÉLECTION DU TYPE DE PRIX */}
              <Grid item xs={12}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel 
                    component="legend" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'text.primary',
                      mb: 1
                    }}
                  >
                    {t('filters.price.typeLabel', 'Type de tarification')}
                  </FormLabel>
                  <ToggleButtonGroup
                    value={priceType}
                    exclusive
                    onChange={handlePriceTypeChange}
                    size="small"
                    sx={{ 
                      display: 'flex',
                      gap: 1,
                      '& .MuiToggleButton-root': {
                        flex: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        transition: 'all 0.3s ease',
                        '&.Mui-selected': {
                          backgroundColor: `${currentPriceType?.color}20`,
                          color: currentPriceType?.color,
                          '&:hover': {
                            backgroundColor: `${currentPriceType?.color}30`
                          }
                        }
                      }
                    }}
                  >
                    {priceTypes.map((type) => (
                      <ToggleButton 
                        key={type.value} 
                        value={type.value}
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: 0.5,
                          p: 1.5
                        }}
                      >
                        {type.icon}
                        <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>
                          {type.label}
                        </Typography>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </FormControl>
              </Grid>

              {/* 📊 SLIDER DE PRIX */}
              <Grid item xs={12}>
                <Box sx={{ px: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 2,
                      color: 'text.primary'
                    }}
                  >
                    {t('filters.price.rangeLabel', 'Gamme de prix')} ({currency})
                  </Typography>
                  
                  {/* Vérification de sécurité pour le slider */}
                  {sliderRange && Array.isArray(sliderRange) && sliderRange.length === 2 ? (
                    <>
                      <Slider
                        value={priceRange}
                        onChange={handleSliderChange}
                        valueLabelDisplay="auto"
                        min={sliderRange[0]}
                        max={sliderRange[1]}
                        step={currentPriceType?.step || 10}
                        valueLabelFormat={(value) => `${Number(value).toLocaleString()} ${currency}`}
                        sx={{
                          color: currentPriceType?.color,
                          '& .MuiSlider-thumb': {
                            backgroundColor: currentPriceType?.color,
                            '&:hover': {
                              boxShadow: `0 0 0 8px ${currentPriceType?.color}20`
                            }
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: currentPriceType?.color
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: `${currentPriceType?.color}20`
                          }
                        }}
                      />
                      
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        mt: 1
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          {Number(sliderRange[0]).toLocaleString()} {currency}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Number(sliderRange[1]).toLocaleString()} {currency}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="error" sx={{ p: 2 }}>
                      Erreur de configuration du slider de prix. Veuillez recharger la page.
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* ⌨️ CHAMPS DE SAISIE MANUELLE */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('filters.price.minLabel', 'Prix minimum')}
                  value={minPrice}
                  onChange={handleMinPriceChange}
                  type="number"
                  error={!!errors.minPrice}
                  helperText={errors.minPrice}
                  inputProps={{ min: 0, max: sliderRange[1] }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency}
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" color="text.secondary">
                          {currentPriceType?.shortLabel}
                        </Typography>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: currentPriceType?.color
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: currentPriceType?.color
                    }
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('filters.price.maxLabel', 'Prix maximum')}
                  value={maxPrice}
                  onChange={handleMaxPriceChange}
                  type="number"
                  error={!!errors.maxPrice || !!errors.range}
                  helperText={errors.maxPrice || errors.range}
                  inputProps={{ min: 0, max: sliderRange[1] }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency}
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" color="text.secondary">
                          {currentPriceType?.shortLabel}
                        </Typography>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: currentPriceType?.color
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: currentPriceType?.color
                    }
                  }}
                />
              </Grid>

              {/* 📈 RÉSUMÉ ACTUEL */}
              <Grid item xs={12}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    backgroundColor: `${currentPriceType?.color}10`,
                    border: `1px solid ${currentPriceType?.color}30`,
                    borderRadius: 2
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('filters.price.summary.title', 'Filtre actuel :')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={currentPriceType?.icon}
                      label={currentPriceType?.label}
                      size="small"
                      sx={{
                        backgroundColor: currentPriceType?.color,
                        color: 'white',
                        fontWeight: 'medium'
                      }}
                    />
                    {(minPrice || maxPrice) && (
                      <Typography
                        variant="body2"
                        color={(errors.minPrice || errors.maxPrice || errors.range) ? 'error.main' : 'text.secondary'}
                      >
                        {errors.minPrice || errors.maxPrice || errors.range
                          ? errors.minPrice || errors.maxPrice || errors.range
                          : (() => {
                              const formattedMin = `${Number(minPrice).toLocaleString()} ${currency}`;
                              const formattedMax = `${Number(maxPrice).toLocaleString()} ${currency}`;
                              if (minPrice && maxPrice) {
                                return t('filters.price.summary.range', '{{min}} - {{max}}', {
                                  min: formattedMin,
                                  max: formattedMax
                                });
                              }
                              if (minPrice) {
                                return t('filters.price.summary.from', 'À partir de {{price}}', {
                                  price: formattedMin
                                });
                              }
                              return t('filters.price.summary.to', "Jusqu'à {{price}}", {
                                price: formattedMax
                              });
                            })()
                        }
                      </Typography>
                    )}
                    {!minPrice && !maxPrice && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {t('filters.price.summary.noLimit', 'Aucune limite de prix')}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      </Collapse>
    </Box>
  );
};

export default PriceFilter;
